import {
  OPEN_DIVISION_KEY,
  placedByDivisionKey,
  sortJudgesForDisplay,
} from "score-common";
import { rideCnoNumber } from "./rideDisplay";

// #2859 - the final-standings rows for a PLACED class, built from the ride
// rows useRidesForClass already denormalizes (judgeScores + composite ride on
// each one). Only rides with a stored placing are standings: a scratched ride,
// or one that never rode, has none and is left out, which is exactly the set
// deriveClassStatus required to call the class PLACED. A stored 0 is the
// explicit "placing step ran, no ribbon" (#1839): it stays listed, after every
// ribboned ride, rather than vanishing from the result.
//
// Sort: ribboned placings ascending, then no-ribbon rides, ties broken by
// ride number so the order is deterministic.
export function buildClassStandings(rides) {
  const placed = (rides ?? []).filter((r) => r?.placing != null);
  const key = (r) => (r.placing > 0 ? r.placing : Number.POSITIVE_INFINITY);
  return placed
    .slice()
    .sort((a, b) => {
      const ka = key(a);
      const kb = key(b);
      if (ka !== kb) return ka < kb ? -1 : 1;
      return (a.rideNumber ?? 0) - (b.rideNumber ?? 0);
    })
    .map((r) => ({
      rideId: r.id,
      placing: r.placing,
      cno: rideCnoNumber(r),
      riderName: r.riderName ?? "",
      riderStatus: r.riderStatus ?? null,
      horseName: r.horseName ?? "",
      judgeScores: r.judgeScores ?? [],
      composite: typeof r.composite === "number" ? r.composite : null,
    }));
}

// One column per judge position that scored anyone in the class, in arena
// order (E, H, C, M, B). A judge with no score on any standing row has no
// column, so a class with two judges seated shows two columns, not five.
export function standingsJudgeColumns(rows) {
  const seen = new Set();
  for (const row of rows)
    for (const js of row.judgeScores) if (js.judgeId) seen.add(js.judgeId);
  return sortJudgesForDisplay([...seen]);
}

// #3380 / #3406 - split standings rows into one group per rider-status
// division, for a class that is Placed by division. The CALLER decides whether
// it is, from the class's own setting (class.placedByDivision === true), the
// same setting score-api's placer and the scoreboard obey, so Scribe can never
// disagree with them. The group key is score-common's placedByDivisionKey
// (trim + case-fold, FVD free text; a ride with NO rider status belongs to
// Open, the same group an explicit "Open" lands in), the same function the
// placer buckets on. Order: named divisions alphabetically, Open among them.
// Rows keep their incoming (placing) order inside each group. Label is the
// first-seen trimmed original casing; null for the Open group (the view
// supplies its text, so a no-status ride and "open " read as one "Open").
export function groupStandingsByDivision(rows) {
  const byKey = new Map();
  for (const row of rows) {
    const key = placedByDivisionKey(row.riderStatus);
    const existing = byKey.get(key);
    if (existing) existing.rows.push(row);
    else
      byKey.set(key, {
        key,
        label: key === OPEN_DIVISION_KEY ? null : row.riderStatus.trim(),
        rows: [row],
      });
  }
  return [...byKey.keys()]
    .sort((a, b) => a.localeCompare(b))
    .map((k) => byKey.get(k));
}
